import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, TextField, Grid, Card, CardContent, Avatar,
  Paper, Stack, Chip, FormControlLabel, Switch, Snackbar, Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningIcon from '@mui/icons-material/Warning';
import CampaignIcon from '@mui/icons-material/Campaign';
import InfoIcon from '@mui/icons-material/Info';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { ConfigContext } from '../shared/ConfigContext';
import AdvertisementClient from '../../api/advertisementClient';
import ImageUpload from '../shared/ImageUpload';
import AdvertisementRenderer from '../shared/AdvertisementRenderer';
import { ADVERTISEMENT_TEMPLATES } from '../../constants/advertisementTemplates';
import { WizardDrawer, FieldRow, useDesignTokens } from '../ui';
import { radius } from '../../themes/smartinventoryTheme';

const TEMPLATE_ICONS = {
  discount_sale: LocalOfferIcon,
  restock: InventoryIcon,
  low_stock: WarningIcon,
  announcement: CampaignIcon,
  service_notice: InfoIcon,
};

const emptyForm = () => ({
  templateType: '',
  title: '',
  badge: '',
  headline: '',
  body: '',
  ctaText: '',
  ctaUrl: '',
  accentColor: '#2196F3',
  priority: 0,
  imageFile: null,
  imageUrl: '',
  imagePreview: '',
  startDate: null,
  endDate: null,
  status: 'draft',
});

// WizardDrawer chrome (Coupons' pattern) for the same 5 steps: Choose
// Template · Fill Details · Upload Image · Preview · Schedule & Publish.
// "Publish immediately" replaces the old dual Save-as-Draft/Publish footer
// buttons — WizardDrawer has one primary action, so the outcome is picked by
// a toggle on the last step instead of a second button.
const AdvertisementWizard = ({ open, onClose, onSuccess, editingAdvertisement, companyId }) => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const { categoryColors } = useDesignTokens();
  const presets = Object.values(categoryColors)
    .filter((tone) => tone.dot !== categoryColors.none.dot)
    .map((tone) => tone.dot);

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState('light');
  const [publishImmediately, setPublishImmediately] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const [formData, setFormData] = useState(emptyForm());
  const initialFormRef = useRef(formData);

  useEffect(() => {
    if (!open) return;
    if (editingAdvertisement) {
      const content = editingAdvertisement.content || {};
      const loaded = {
        templateType: editingAdvertisement.templateType || '',
        title: editingAdvertisement.title || '',
        badge: content.badge || '',
        headline: content.headline || '',
        body: content.body || '',
        ctaText: content.ctaText || '',
        ctaUrl: content.ctaUrl || '',
        accentColor: editingAdvertisement.accentColor || '#2196F3',
        priority: editingAdvertisement.priority || 0,
        imageFile: null,
        imageUrl: editingAdvertisement.imageUrl || '',
        imagePreview: editingAdvertisement.imageUrl || '',
        startDate: editingAdvertisement.startsAt ? new Date(editingAdvertisement.startsAt) : null,
        endDate: editingAdvertisement.endsAt ? new Date(editingAdvertisement.endsAt) : null,
        status: editingAdvertisement.status || 'draft',
      };
      setFormData(loaded);
      initialFormRef.current = loaded;
      setPublishImmediately(loaded.status === 'active');
    } else {
      const fresh = emptyForm();
      setFormData(fresh);
      initialFormRef.current = fresh;
      setPublishImmediately(false);
    }
    setActiveStep(0);
  }, [editingAdvertisement, open]);

  const steps = [
    { label: t('Choose Template') },
    { label: t('Fill Details') },
    { label: t('Upload Image') },
    { label: t('Preview') },
    { label: t('Schedule & Publish') },
  ];

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialFormRef.current) || publishImmediately !== (initialFormRef.current.status === 'active');

  const handleTemplateSelect = (templateId) => {
    const template = ADVERTISEMENT_TEMPLATES[templateId];
    setFormData({
      ...formData,
      templateType: templateId,
      badge: template.defaults.badge,
      headline: template.defaults.headline,
      body: template.defaults.body,
      ctaText: template.defaults.ctaText,
      accentColor: template.accentColor,
    });
  };

  const handleImageSelected = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, imageFile: file, imagePreview: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleImageRemoved = () => {
    setFormData((prev) => ({ ...prev, imageFile: null, imageUrl: '', imagePreview: '' }));
  };

  const stepInvalid = () => {
    if (activeStep === 0) return !formData.templateType;
    if (activeStep === 1) return !formData.title || !formData.headline;
    return false;
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const client = new AdvertisementClient(config.backendUrl);

      let imageUrl = formData.imageUrl;
      if (formData.imageFile) {
        setUploading(true);
        const uploadResponse = await client.uploadAdvertisementImage(formData.imageFile, companyId);
        imageUrl = uploadResponse.data.imageUrl;
        setUploading(false);
      }

      const adData = {
        companyId,
        templateType: formData.templateType,
        title: formData.title,
        content: {
          badge: formData.badge,
          headline: formData.headline,
          body: formData.body,
          ctaText: formData.ctaText,
          ctaUrl: formData.ctaUrl,
        },
        accentColor: formData.accentColor,
        priority: parseInt(formData.priority, 10) || 0,
        imageUrl,
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: publishImmediately ? 'active' : (editingAdvertisement ? formData.status : 'draft'),
      };

      if (editingAdvertisement) {
        await client.updateAdvertisement(editingAdvertisement._id, adData);
        onSuccess(t('Advertisement updated successfully'));
      } else {
        await client.createAdvertisement(adData);
        onSuccess(t('Advertisement created successfully'));
      }
    } catch (error) {
      console.error('Error saving advertisement:', error);
      setSnackbar({ open: true, message: t('Failed to save advertisement') });
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const getTemplateIcon = (templateType) => {
    const Icon = TEMPLATE_ICONS[templateType] || CampaignIcon;
    return <Icon />;
  };

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Select a template to get started')}
          </Typography>
          <Grid container spacing={2}>
            {Object.values(ADVERTISEMENT_TEMPLATES).map((template) => (
              <Grid item xs={12} sm={6} key={template.id}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    borderColor: formData.templateType === template.id ? template.accentColor : 'divider',
                    borderWidth: 2,
                    bgcolor: formData.templateType === template.id ? alpha(template.accentColor, 0.06) : 'transparent',
                  }}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ bgcolor: template.accentColor, width: 40, height: 40, mr: 1.5 }}>
                        {getTemplateIcon(template.id)}
                      </Avatar>
                      {formData.templateType === template.id && (
                        <CheckCircleIcon fontSize="small" sx={{ color: template.accentColor, ml: 'auto' }} />
                      )}
                    </Box>
                    <Typography variant="subtitle2">{t(template.name)}</Typography>
                    <Typography variant="caption" color="text.secondary">{t(template.description)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    if (activeStep === 1) {
      return (
        <Box>
          <FieldRow label={t('Title')} required helper={`${formData.title.length}/200`}>
            <TextField
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              inputProps={{ maxLength: 200 }}
            />
          </FieldRow>
          <FieldRow label={t('Badge')}>
            <TextField fullWidth value={formData.badge} onChange={(e) => setFormData({ ...formData, badge: e.target.value })} placeholder="SALE, NEW, etc." />
          </FieldRow>
          <FieldRow label={t('Headline')} required>
            <TextField fullWidth value={formData.headline} onChange={(e) => setFormData({ ...formData, headline: e.target.value })} />
          </FieldRow>
          <FieldRow label={t('Body')}>
            <TextField fullWidth multiline rows={3} value={formData.body} onChange={(e) => setFormData({ ...formData, body: e.target.value })} />
          </FieldRow>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FieldRow label={t('CTA Button Text')}>
                <TextField fullWidth value={formData.ctaText} onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })} />
              </FieldRow>
            </Grid>
            <Grid item xs={6}>
              <FieldRow label={t('CTA Button URL')}>
                <TextField fullWidth value={formData.ctaUrl} onChange={(e) => setFormData({ ...formData, ctaUrl: e.target.value })} placeholder="https://..." />
              </FieldRow>
            </Grid>
          </Grid>
          <FieldRow label={t('Accent Color')}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              {presets.map((hex) => {
                const selected = formData.accentColor?.toLowerCase() === hex.toLowerCase();
                return (
                  <Box
                    key={hex}
                    onClick={() => setFormData({ ...formData, accentColor: hex })}
                    sx={{
                      width: 32, height: 32, borderRadius: `${radius.sm}px`, bgcolor: hex, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '2px solid', borderColor: selected ? 'text.primary' : 'transparent',
                    }}
                  >
                    {selected && <CheckIcon fontSize="small" sx={{ color: 'common.white' }} />}
                  </Box>
                );
              })}
            </Box>
            <TextField
              fullWidth
              value={formData.accentColor}
              onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
              InputProps={{
                startAdornment: <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: formData.accentColor, mr: 1, flexShrink: 0, border: '1px solid', borderColor: 'divider' }} />,
              }}
            />
          </FieldRow>
          <FieldRow label={t('Priority')} helper={t('Higher priority ads are shown first')}>
            <TextField fullWidth type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} />
          </FieldRow>
        </Box>
      );
    }

    if (activeStep === 2) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Add an image to make your advertisement more engaging')}
          </Typography>
          <ImageUpload
            onImageSelected={handleImageSelected}
            onImageRemoved={handleImageRemoved}
            initialImage={formData.imagePreview || formData.imageUrl}
            uploading={uploading}
          />
        </Box>
      );
    }

    if (activeStep === 3) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('This is how your advertisement will appear to users')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Chip icon={<LightModeIcon />} label={t('Light Mode')} onClick={() => setPreviewMode('light')} color={previewMode === 'light' ? 'primary' : 'default'} size="small" />
            <Chip icon={<DarkModeIcon />} label={t('Dark Mode')} onClick={() => setPreviewMode('dark')} color={previewMode === 'dark' ? 'primary' : 'default'} size="small" />
          </Stack>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <AdvertisementRenderer
              advertisement={{
                ...formData,
                imageUrl: formData.imagePreview || formData.imageUrl,
                _id: 'preview',
                id: 'preview',
              }}
              onDismiss={() => {}}
            />
          </Paper>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('Set when this advertisement should be visible')}
        </Typography>
        <FieldRow label={t('Start Date')}>
          <TextField
            fullWidth
            type="datetime-local"
            value={formData.startDate instanceof Date && !isNaN(formData.startDate) ? formData.startDate.toISOString().slice(0, 16) : ''}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value ? new Date(e.target.value) : null })}
            InputLabelProps={{ shrink: true }}
            disabled={!formData.startDate}
          />
          <FormControlLabel
            control={<Switch checked={!formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.checked ? null : new Date() })} />}
            label={t('Start showing immediately')}
          />
        </FieldRow>
        <FieldRow label={t('End Date')}>
          <TextField
            fullWidth
            type="datetime-local"
            value={formData.endDate instanceof Date && !isNaN(formData.endDate) ? formData.endDate.toISOString().slice(0, 16) : ''}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value ? new Date(e.target.value) : null })}
            InputLabelProps={{ shrink: true }}
            disabled={!formData.endDate}
          />
          <FormControlLabel
            control={<Switch checked={!formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.checked ? null : new Date() })} />}
            label={t('No end date')}
          />
        </FieldRow>
        <FieldRow label={t('Publish immediately')} helper={t('Off saves as a draft you can activate later')}>
          <FormControlLabel
            control={<Switch checked={publishImmediately} onChange={(e) => setPublishImmediately(e.target.checked)} />}
            label={publishImmediately ? t('Active') : t('Draft')}
          />
        </FieldRow>
      </Box>
    );
  };

  return (
    <>
      <WizardDrawer
        open={open}
        onClose={onClose}
        title={editingAdvertisement ? t('Edit Advertisement') : t('Create Advertisement')}
        steps={steps}
        activeStep={activeStep}
        onStepClick={setActiveStep}
        closeLabel={t('Close', { defaultValue: 'Close' })}
        backLabel={t('Back')}
        nextLabel={t('Next')}
        finalLabel={saving
          ? t('Saving...')
          : (editingAdvertisement ? t('Save changes', { defaultValue: 'Save changes' }) : t('Create Advertisement'))}
        onBack={() => setActiveStep((s) => s - 1)}
        onNext={() => setActiveStep((s) => s + 1)}
        onFinish={handleSave}
        nextDisabled={saving || stepInvalid()}
        isDirty={isDirty}
        discardConfirm={{
          title: t('Discard changes?', { defaultValue: 'Discard changes?' }),
          description: t('Are you sure you want to discard your changes?'),
          cancelLabel: t('Keep editing', { defaultValue: 'Keep editing' }),
          confirmLabel: t('Discard'),
        }}
      >
        {renderStepContent()}
      </WizardDrawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default AdvertisementWizard;
